import { ComponentProps, FC, Fragment, ReactElement, ReactNode, useMemo } from "react";

import Text from "components/Text/Text";
import { ClassName, OR, OrString, ValueOrArray } from "config/types";
import { ConditionalWrapper } from "modules";

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import classnames from "classnames";
import { find, flatten, get, isEmpty, isNil, isPlainObject, map, partition } from "lodash-es";

type Item<C = unknown> = {
  id: string;
  content: Record<string, C>;
};

interface ItemProps extends Record<string, unknown> {
  className?: ClassName;
  dragging?: Record<string, unknown>;
}

type TitleProp = OR<OrString<ComponentProps<typeof Text>>, ReactNode>;

type ListState = {
  collection: Item<ComponentProps<ListState["ListItem"]>>[];
  ListItem: FC<any>;
  itemProps?: ItemProps;
  title?: ValueOrArray<TitleProp>;
};

export type DragResult = {
  draggableId: string;
  source: { droppableId: string; index: number };
  destination: { droppableId: string; index: number } | null;
};

interface IProps {
  type: string;
  list: ListState;
  className?: ClassName;
  partition?: Record<string, unknown>;
  onDragEnd?(dragEvent: DragResult): void;
}

interface SortableItemProps {
  id: string;
  ListItem: FC<any>;
  content: Record<string, unknown>;
  itemClasses?: ClassName;
  draggingItemClass?: ClassName;
  restItemProps: Record<string, unknown>;
  restDragItemProps: Record<string, unknown>;
}

const SortableItem: FC<SortableItemProps> = ({
  id,
  ListItem,
  content,
  itemClasses,
  draggingItemClass,
  restItemProps,
  restDragItemProps,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      className="dragdrop-list__item-container"
      style={style}
      {...attributes}
      {...listeners}>
      <ListItem
        {...restItemProps}
        {...(isDragging ? restDragItemProps : {})}
        {...content}
        className={classnames(
          itemClasses,
          "dragdrop-list__item-wrapper",
          isDragging ? (draggingItemClass as ClassName) : ""
        )}
      />
    </div>
  );
};

export const DraggableList: FC<IProps> = props => {
  const { className, list, onDragEnd, partition: partitionCondition } = props;
  const { collection, itemProps = {}, ListItem, title: listTitle } = list;
  const listTitles = flatten([listTitle]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const collectionList = useMemo(
    () =>
      isEmpty(partitionCondition) ? [collection] : partition(collection, partitionCondition as any),
    [collection, partitionCondition]
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const activeId = String(active.id);
    const overId = String(over.id);

    // Find which bucket the active item belongs to
    let sourceBucketIndex = -1;
    let sourceIndex = -1;
    let destBucketIndex = -1;
    let destIndex = -1;

    for (let i = 0; i < collectionList.length; i++) {
      const bucket = collectionList[i];
      const activeIdx = bucket.findIndex(item => item.id === activeId);
      if (activeIdx !== -1) {
        sourceBucketIndex = i;
        sourceIndex = activeIdx;
      }
      const overIdx = bucket.findIndex(item => item.id === overId);
      if (overIdx !== -1) {
        destBucketIndex = i;
        destIndex = overIdx;
      }
    }

    if (sourceBucketIndex === -1 || destBucketIndex === -1) {
      return;
    }

    // Restrict cross-bucket drags when partition is active
    const differentBucket = sourceBucketIndex !== destBucketIndex;
    if (!isEmpty(partitionCondition) && differentBucket) {
      const [firstBucket] = collectionList;
      const resultExistsInFirstBucket = find(firstBucket, { id: activeId });
      if (isEmpty(resultExistsInFirstBucket)) {
        return;
      }
    }

    const result: DragResult = {
      draggableId: activeId,
      source: { droppableId: `droppable-${sourceBucketIndex + 1}`, index: sourceIndex },
      destination: { droppableId: `droppable-${destBucketIndex + 1}`, index: destIndex },
    };

    onDragEnd && onDragEnd(result);
  };

  const baseListClass = "dragdrop-list__container";
  const { dragging: draggingItemProps = {}, className: itemClasses, ...restItemProps } = itemProps;
  const { className: draggingItemClass, ...restDragItemProps } = draggingItemProps;

  const buildTextElement = (
    text: TitleProp,
    baseClassName?: ClassName
  ): ReactElement<ComponentProps<typeof Text>, typeof Text> => {
    const defaultTag: ComponentProps<typeof Text>["tag"] = "c5";
    const propsFromText = isPlainObject(text)
      ? (text as ComponentProps<typeof Text>)
      : { className: "" };
    const { className: classNameFromProps, ...restProps } = propsFromText;
    const className = classnames(baseClassName, get(text, "className", classNameFromProps));
    const textProps: ComponentProps<typeof Text> = {
      className,
      tag: defaultTag,
      ...restProps,
    };

    return <Text {...textProps}>{text as ReactNode}</Text>;
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      {map(collectionList, (bucketCollection, index) => {
        const itemIds = map(bucketCollection, item => item.id);
        return (
          <Fragment key={index}>
            <ConditionalWrapper
              condition={!isEmpty(listTitles[index]) || !isNil(listTitles[index])}>
              {buildTextElement(listTitles[index])}
            </ConditionalWrapper>
            <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
              <div className={classnames(baseListClass, className)}>
                <div className="dragdrop-list__wrapper">
                  {map(bucketCollection, item => (
                    <SortableItem
                      key={item.id}
                      id={item.id}
                      ListItem={ListItem}
                      content={item.content}
                      itemClasses={itemClasses}
                      draggingItemClass={draggingItemClass as ClassName}
                      restItemProps={restItemProps}
                      restDragItemProps={restDragItemProps}
                    />
                  ))}
                </div>
              </div>
            </SortableContext>
          </Fragment>
        );
      })}
    </DndContext>
  );
};
